import React from 'react';
import {
  Users, X, Plus, Check, CheckCircle, AlertCircle,
  Banknote, CreditCard, Printer
} from 'lucide-react';
import { formatPrice } from '../../utils/translations';

export const SplitBillModal = ({
  selectedTable, showSplitBill, setShowSplitBill,
  splitPeople, setSplitPeople, activeSplitPerson, setActiveSplitPerson,
  splitResult, setSplitResult, billDiscountPercent, setBillDiscountPercent,
  settings, getSplitAllItems, toggleItemForPerson, addSplitPerson,
  removeSplitPerson, splitEvenly, confirmSplitBill, closeSplitBill, t
}) => {
  if (!showSplitBill || !selectedTable) return null;

  const allItems = getSplitAllItems();
  const assignedSet = new Set(splitPeople.flatMap(p => p.itemIndices));
  const unassigned = allItems.map((_, i) => i).filter(i => !assignedSet.has(i));
  const cardFeePct = settings?.credit_card_fee_percent || 3;
  const ccEnabled = settings?.enable_credit_card_fee;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="split-bill-modal">
      <div className="bg-slate-800 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-5 flex items-center justify-between rounded-t-3xl z-10">
          <h2 className="text-xl font-black flex items-center gap-2">
            <Users size={20} className="text-blue-400" />
            {t.splitBillTitle} -- {t.table} {selectedTable.table}
          </h2>
          <button onClick={() => { setShowSplitBill(false); setSplitResult(null); }} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="close-split-modal">
            <X size={20} />
          </button>
        </div>

        {!splitResult ? (
          <div className="p-5">
            {/* Discount row */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-slate-400">{t.discount}:</span>
              {[0, 5, 10, 15, 20].map(pct => (
                <button key={pct}
                  onClick={() => setBillDiscountPercent(pct)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold ${billDiscountPercent === pct ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-300'}`}
                >{pct}%</button>
              ))}
            </div>

            {/* Split Evenly quick action */}
            <button
              onClick={splitEvenly}
              className="w-full mb-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-xl font-bold text-sm hover:from-violet-500 hover:to-indigo-500 flex items-center justify-center gap-2 transition-all"
              data-testid="split-evenly-btn"
            >
              <Users size={16} /> {t.splitEvenly} -- {t.splitEvenlyDesc}
            </button>

            {/* People tabs */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {splitPeople.map((person, i) => (
                <button key={i}
                  onClick={() => setActiveSplitPerson(i)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
                    activeSplitPerson === i ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  data-testid={`split-person-tab-${i}`}
                >
                  <Users size={14} />
                  {person.label}
                  <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px]">{person.itemIndices.length}</span>
                  {splitPeople.length > 2 && (
                    <span onClick={(e) => { e.stopPropagation(); removeSplitPerson(i); }} className="ml-1 hover:text-red-400">
                      <X size={12} />
                    </span>
                  )}
                </button>
              ))}
              <button onClick={addSplitPerson}
                className="px-3 py-2 rounded-xl text-sm font-bold bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center gap-1"
                data-testid="add-split-person-btn"
              >
                <Plus size={14} /> {t.addPerson}
              </button>
            </div>

            {/* Payment method for active person */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-slate-400">{splitPeople[activeSplitPerson]?.label} {t.paymentMethod}:</span>
              <button
                onClick={() => setSplitPeople(prev => prev.map((p, i) => i === activeSplitPerson ? { ...p, paymentMethod: 'cash' } : p))}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${
                  splitPeople[activeSplitPerson]?.paymentMethod === 'cash' ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-300'
                }`}
              ><Banknote size={12} /> {t.cash}</button>
              <button
                onClick={() => setSplitPeople(prev => prev.map((p, i) => i === activeSplitPerson ? { ...p, paymentMethod: 'card' } : p))}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${
                  splitPeople[activeSplitPerson]?.paymentMethod === 'card' ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-300'
                }`}
              ><CreditCard size={12} /> {t.card}</button>
            </div>

            {/* Items grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {allItems.map((item, idx) => {
                const assignedTo = splitPeople.findIndex(p => p.itemIndices.includes(idx));
                const isActive = assignedTo === activeSplitPerson;
                const isOther = assignedTo >= 0 && assignedTo !== activeSplitPerson;
                return (
                  <button key={idx}
                    onClick={() => toggleItemForPerson(idx)}
                    className={`p-3 rounded-xl text-left flex items-center justify-between transition-all border ${
                      isActive ? 'bg-blue-600/20 border-blue-500 text-white' :
                      isOther ? 'bg-slate-700/50 border-slate-600 text-slate-500' :
                      'bg-slate-700 border-slate-600 text-slate-200 hover:border-blue-500'
                    }`}
                    data-testid={`split-item-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{item.name?.en || item.name}</div>
                      <div className="text-xs opacity-60">x{item.quantity} - {formatPrice(item.price * item.quantity)}</div>
                    </div>
                    {assignedTo >= 0 && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${
                        isActive ? 'bg-blue-500 text-white' : 'bg-slate-600 text-slate-300'
                      }`}>{splitPeople[assignedTo].label}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Unassigned warning */}
            {unassigned.length > 0 && (
              <p className="text-amber-400 text-xs mt-3 flex items-center gap-1">
                <AlertCircle size={12} /> {unassigned.length} {t.unassigned.toLowerCase()}
              </p>
            )}

            {/* Per-person summaries */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {splitPeople.map((person, i) => {
                const pItems = person.itemIndices.map(idx => allItems[idx]).filter(Boolean);
                const sub = pItems.reduce((s, it) => s + (it.price * it.quantity), 0);
                const disc = Math.round(sub * billDiscountPercent / 100);
                const afterDisc = sub - disc;
                const fee = person.paymentMethod === 'card' && ccEnabled ? Math.round(afterDisc * cardFeePct / 100) : 0;
                const personTotal = afterDisc + fee;
                return (
                  <div key={i} className={`bg-slate-700/50 rounded-xl p-3 border ${activeSplitPerson === i ? 'border-blue-500' : 'border-slate-600'}`}>
                    <div className="font-bold text-sm mb-1">{person.label}</div>
                    <div className="text-xs text-slate-400">{pItems.length} items - {person.paymentMethod === 'card' ? t.card : t.cash}</div>
                    <div className="text-lg font-black text-amber-400 mt-1">{formatPrice(personTotal)}</div>
                  </div>
                );
              })}
            </div>

            {/* Confirm */}
            <button
              onClick={confirmSplitBill}
              className="w-full mt-5 py-3 bg-blue-600 rounded-xl font-black text-lg hover:bg-blue-500 flex items-center justify-center gap-2"
              data-testid="confirm-split-btn"
            >
              <CheckCircle size={20} /> {t.confirmSplit}
            </button>
          </div>
        ) : (
          /* Split Result */
          <div className="p-5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check size={32} />
              </div>
              <h3 className="text-xl font-black">{t.splitSummary}</h3>
              <p className="text-sm text-slate-400 mt-1">Grand Total: <span className="text-amber-400 font-bold">{formatPrice(splitResult.grand_total)}</span></p>
            </div>

            <div className="space-y-3 mb-6">
              {splitResult.splits.map((split, i) => (
                <div key={i} className="bg-slate-700 rounded-xl p-4 border border-slate-600" data-testid={`split-result-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">{split.label}</span>
                    <span className="text-amber-400 font-black text-lg">{formatPrice(split.total)}</span>
                  </div>
                  <div className="text-xs text-slate-400 space-y-0.5">
                    {split.items.map((item, j) => (
                      <div key={j} className="flex justify-between">
                        <span>{item.name?.en || item.name} x{item.quantity}</span>
                        <span>{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-600 mt-2 pt-2 text-xs">
                    <div className="flex justify-between text-slate-400">
                      <span>{t.subtotal}</span><span>{formatPrice(split.subtotal)}</span>
                    </div>
                    {split.discount_amount > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>{t.discount} ({split.discount_percent}%)</span><span>-{formatPrice(split.discount_amount)}</span>
                      </div>
                    )}
                    {split.card_fee > 0 && (
                      <div className="flex justify-between text-blue-400">
                        <span>{t.cardFee} ({split.card_fee_percent}%)</span><span>+{formatPrice(split.card_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-white mt-1">
                      <span>{split.payment_method === 'card' ? t.card : t.cash}</span>
                      <span>{formatPrice(split.total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={closeSplitBill}
              className="w-full py-3 bg-amber-500 text-slate-900 rounded-xl font-black text-lg hover:bg-amber-400 flex items-center justify-center gap-2"
              data-testid="print-split-bills-btn"
            >
              <Printer size={20} /> {t.printAll}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
